import React, { createContext, useContext, useState, useCallback } from 'react';
import {
    View,
    Text,
    TouchableOpacity,
    Modal,
    StyleSheet,
    Platform,
    Alert as RNAlert,
} from 'react-native';
import { COLORS } from '../config';

// Context for custom alerts
const AlertContext = createContext(null);

export const useCustomAlert = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useCustomAlert must be used within AlertProvider');
    }
    return context;
};

// Cross-platform alert function
export const showAlert = (title, message, buttons = [{ text: 'OK' }], contextAlert = null) => {
    // On native platforms, use RNAlert
    if (Platform.OS !== 'web') {
        RNAlert.alert(title, message, buttons);
        return;
    }

    // On web, use context-based modal alert if available
    if (contextAlert) {
        contextAlert(title, message, buttons);
        return;
    }

    // Fallback to browser alert for web
    const buttonText = buttons.map(b => b.text).join(' / ');
    const result = window.confirm(`${title}\n\n${message}`);

    if (result && buttons[0]?.onPress) {
        buttons[0].onPress();
    } else if (!result && buttons.length > 1 && buttons[1]?.onPress) {
        buttons[1].onPress();
    }
};

// Alert Provider Component
export const AlertProvider = ({ children }) => {
    const [alertVisible, setAlertVisible] = useState(false);
    const [alertConfig, setAlertConfig] = useState({
        title: '',
        message: '',
        buttons: [],
    });

    const showAlertModal = useCallback((title, message, buttons = [{ text: 'OK' }]) => {
        setAlertConfig({ title, message, buttons });
        setAlertVisible(true);
    }, []);

    const handleButtonPress = useCallback((button) => {
        setAlertVisible(false);
        if (button.onPress) {
            setTimeout(() => button.onPress(), 100);
        }
    }, []);

    const alert = useCallback((title, message, buttons) => {
        if (Platform.OS !== 'web') {
            RNAlert.alert(title, message, buttons);
        } else {
            showAlertModal(title, message, buttons);
        }
    }, [showAlertModal]);

    return (
        <AlertContext.Provider value={{ alert }}>
            {children}

            {/* Web Alert Modal */}
            <Modal
                visible={alertVisible}
                transparent
                animationType="fade"
                onRequestClose={() => setAlertVisible(false)}
            >
                <View style={styles.overlay}>
                    <View style={styles.alertBox}>
                        {alertConfig.title ? (
                            <Text style={styles.title}>{alertConfig.title}</Text>
                        ) : null}

                        {alertConfig.message ? (
                            <Text style={styles.message}>{alertConfig.message}</Text>
                        ) : null}

                        <View style={styles.buttonContainer}>
                            {alertConfig.buttons.map((button, index) => (
                                <TouchableOpacity
                                    key={index}
                                    style={[
                                        styles.button,
                                        index === alertConfig.buttons.length - 1 && styles.buttonPrimary,
                                        alertConfig.buttons.length === 1 && styles.buttonFull,
                                    ]}
                                    onPress={() => handleButtonPress(button)}
                                >
                                    <Text style={[
                                        styles.buttonText,
                                        index === alertConfig.buttons.length - 1 && styles.buttonTextPrimary,
                                    ]}>
                                        {button.text}
                                    </Text>
                                </TouchableOpacity>
                            ))}
                        </View>
                    </View>
                </View>
            </Modal>
        </AlertContext.Provider>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
    },
    alertBox: {
        backgroundColor: COLORS.bgCard,
        borderRadius: 16,
        padding: 24,
        width: '100%',
        maxWidth: 320,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.25,
        shadowRadius: 20,
        elevation: 10,
    },
    title: {
        fontSize: 18,
        fontWeight: '700',
        color: COLORS.textPrimary,
        textAlign: 'center',
        marginBottom: 12,
    },
    message: {
        fontSize: 14,
        color: COLORS.textSecondary,
        textAlign: 'center',
        lineHeight: 22,
        marginBottom: 24,
    },
    buttonContainer: {
        flexDirection: 'row',
        gap: 12,
    },
    button: {
        flex: 1,
        paddingVertical: 14,
        paddingHorizontal: 20,
        borderRadius: 12,
        backgroundColor: COLORS.bgDark,
        alignItems: 'center',
    },
    buttonPrimary: {
        backgroundColor: COLORS.primary,
    },
    buttonFull: {
        flex: 1,
    },
    buttonText: {
        fontSize: 15,
        fontWeight: '600',
        color: COLORS.textSecondary,
    },
    buttonTextPrimary: {
        color: '#fff',
    },
});

export default AlertProvider;
